import { Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef ,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpParams } from '@angular/common/http';
import { ActionService } from '../../shared/_services/action.service';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../shared/workspace-element-context';

interface InputParams {
  submission_id: string;
  clientKey: number;
  tax_year: number;
  scenario: number;
}

@Component({
    selector: 'review-diagnostics',
    templateUrl: './review-diagnostics.component.html',
    styleUrls: ['./review-diagnostics.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ReviewDiagnostics implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private subs = new Subscription();

  lastReviewedDate: string;

  private _params: InputParams | undefined;

  /**
   * Host-supplied context from the legacy AngularJS template
   * (`<gtw-pdf-review-diagnostics-ce params="{{params}}">`) and the admin
   * wrapper (`<gtw-pdf-review-diagnostics-ce [attr.params]="paramsJson">`).
   *
   * Takes precedence over `WorkspaceService.activeScreen` because the
   * component is also opened from code-invoke paths where `activeScreen`
   * is empty (submit-confirmation modal, formViewCtrl pre-publish
   * diagnostics check). Without this, `submission_id` / `clientKey` /
   * `tax_year` / `scenario` come back zero / empty and `loadData()`
   * never runs.
   */
  private hostParams: any = {};

  /**
   * `params` attribute carries `{submission_id, clientKey | client_key,
   * tax_year, scenario}`. Tolerates both string-encoded JSON (legacy CE
   * attr) and plain objects (Angular property binding). Re-runs
   * {@link applyWorkspaceContext} so the cached `_params` snapshot picks
   * up the host context when the active workspace screen has none.
   */
  @Input('params')
  set _paramsAttr(value: any) {
    this.hostParams = this.parseBoundContext(value);
    this.applyWorkspaceContext();
  }

  gtwApiUrl: string;

  @Output('on-data-loaded')
  onDataLoaded: EventEmitter<boolean>;

  @Output('on-cancel')
  onCancel: EventEmitter<void>;

  @Output('on-ignore')
  onIgnore: EventEmitter<void>;

  get visibleColumnFields(): string[] {
    return this.columns
      .filter(col => col.data != null && col.visible)
      .map(col => col.data as string);
  }

  columns = [
    {
      title: 'Diagnostic Name',
      data: 'DIAG_NAME',
      searchable: false,
      formatAmount: false,
      visible: true,
    },
    {
      title: 'Failed Count',
      data: 'DIAG_FAILED_CNT',
      searchable: false,
      formatAmount: false,
      visible: true,
      width: '145px',
    },
  ];

  data = [];

  isLoading: boolean = false;
  ignore: boolean = false;
  hasDiagnostics: boolean = false;
  showBulkMessage: boolean = false;

  constructor(
    private actionService: ActionService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {
    this.lastReviewedDate = '12/2/2024 23:34';
    this.gtwApiUrl = '/gtw';
    this.onDataLoaded = new EventEmitter();
    this.onCancel = new EventEmitter();
    this.onIgnore = new EventEmitter();
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext()));
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext()));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.gtwApiUrl = gc.base_url || gc.api || '/gtw';
    const row = rowPayloadFromActiveScreen(activeScreen);
    const fp = normalizeFilterParams(activeScreen);
    const d = activeScreen.data || {};
    // Host-supplied `params` attribute wins over `activeScreen` so the
    // submit-confirmation / formViewCtrl pre-publish path (where
    // `activeScreen` is empty) still feeds `submission_id` / `clientKey` /
    // `tax_year` / `scenario` to `loadData`. Mirrors the legacy AngularJS
    // contract: `params="{{params}}"` is the explicit caller payload, not
    // a fallback.
    const hp = this.hostParams || {};
    const ck = Number(
      hp.clientKey ??
        hp.client_key ??
        d.clientKey ??
        d.client_key ??
        fp.client_key ??
        this.gtwApp.USER_SETTINGS?.user?.client_key ??
        0
    );
    const subId = String(
      hp.submission_id ??
        hp.SUBMISSION_ID ??
        d.submission_id ??
        d.SUBMISSION_ID ??
        row?.SUBMISSION_ID ??
        row?.submission_id ??
        fp.submission_id ??
        ''
    );
    const ty = Number(
      hp.tax_year ?? hp.TAX_YEAR ?? d.tax_year ?? fp.tax_year ?? row?.TAX_YEAR ?? 0
    );
    const sc = Number(
      hp.scenario ?? hp.SCENARIO ?? d.scenario ?? fp.scenario ?? row?.SCENARIO ?? 0
    );
    if (subId) {
      this._params = {
        submission_id: subId,
        clientKey: ck,
        tax_year: ty,
        scenario: sc,
      };
      this.loadData();
    } else {
      this._params = undefined;
    }
  }

  /**
   * Parse a CE attribute payload. Accepts JSON-encoded strings (legacy
   * `params="{{... | json}}"` / `setAttribute('params', JSON.stringify(...))`
   * paths) and plain objects (Angular property binding). Mirrors the
   * tolerance pattern used by `xml-vs-elements.component.ts` so the
   * component behaves the same regardless of caller.
   */
  private parseBoundContext(val: any): any {
    if (val == null || val === '') {
      return {};
    }
    if (typeof val === 'object' && !Array.isArray(val)) {
      return { ...val };
    }
    const s = String(val).trim();
    if (!s || s === '[object Object]') {
      return {};
    }
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const first = parsed[0];
        return typeof first === 'object' && first !== null ? { ...first } : {};
      }
      return typeof parsed === 'object' && parsed !== null ? { ...parsed } : {};
    } catch {
      return {};
    }
  }

  loadData() {
    if (!this._params) return;

    const params = new HttpParams({
      fromObject: {
        submission_id: this._params.submission_id.toString(),
        tax_year: this._params.tax_year.toString(),
        scenario: this._params.scenario.toString(),
        jcd_key: '250',
      },
    });

    this.isLoading = true;
    this.actionService.getActionData(this.gtwApiUrl, 31996, this._params?.clientKey, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.isLoading = false;
      this.data = data.jsonObject.filter((d: any) => d.DIAG_FAILED_CNT > 0);
      this.hasDiagnostics = this.data.length > 0;

      this.showBulkMessage = this._params!.submission_id.includes(',') && this.hasDiagnostics;

      this.onDataLoaded.emit(this.hasDiagnostics);
    });
  }

  cancel() {
    this.onCancel.emit();
  }

  doIgnore() {
    this.onIgnore.emit();
    this.ignore = true;
  }
}
